/* - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -  */
/* Vincenty Inverse Solution of Geodesics on the Ellipsoid (c) Chris Veness 2002-2012             */
/* www.movable-type.co.uk/scripts/latlong-vincenty.html                                           */
/* from: Vincenty inverse formula - T Vincenty, "Direct and Inverse Solutions of Geodesics on the */
/*       Ellipsoid with application of nested equations", Survey Review, vol XXII no 176, 1975    */
/*       http://www.ngs.noaa.gov/PUBS_LIB/inverse.pdf                                             */
/* - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -  */

function toRad(value) {
  /** Converts numeric degrees to radians */
  return value * Math.PI / 180;
}

/**
 * Calculates geodetic distance between two points specified by latitude/longitude using
 * Vincenty inverse formula for ellipsoids
 *
 * @param   {number} lat1 - latitude first point, in decimal degrees
 * @param   {number} lon1 - longitude first point, in decimal degrees
 * @param   {number} lat2 - latitude second point, in decimal degrees
 * @param   {number} lon2 - longitude second point, in decimal degrees
 * @returns {number} - distance in metres between points
 */

export function distVincenty(lat1, lon1, lat2, lon2) {
  let a = 6378137, b = 6356752.314245,  f = 1/298.257223563;  // WGS-84 ellipsoid params
  let L = toRad(lon2-lon1);
  let U1 = Math.atan((1-f) * Math.tan(toRad(lat1)));
  let U2 = Math.atan((1-f) * Math.tan(toRad(lat2)));
  let sinU1 = Math.sin(U1), cosU1 = Math.cos(U1);
  let sinU2 = Math.sin(U2), cosU2 = Math.cos(U2);

  let lambda = L, lambdaP, iterLimit = 100;
  let cosSqAlpha, sinSigma, cos2SigmaM, cosSigma, sigma;
  do {
    let sinLambda = Math.sin(lambda), cosLambda = Math.cos(lambda);
    sinSigma = Math.sqrt((cosU2*sinLambda) * (cosU2*sinLambda) +
      (cosU1*sinU2-sinU1*cosU2*cosLambda) * (cosU1*sinU2-sinU1*cosU2*cosLambda));
    if (sinSigma===0) return 0;  // co-incident points
    cosSigma = sinU1*sinU2 + cosU1*cosU2*cosLambda;
    sigma = Math.atan2(sinSigma, cosSigma);
    let sinAlpha = cosU1 * cosU2 * sinLambda / sinSigma;
    cosSqAlpha = 1 - sinAlpha*sinAlpha;
    cos2SigmaM = cosSigma - 2*sinU1*sinU2/cosSqAlpha;
    if (isNaN(cos2SigmaM)) cos2SigmaM = 0;  // equatorial line: cosSqAlpha=0 (§6)
    let C = f/16*cosSqAlpha*(4+f*(4-3*cosSqAlpha));
    lambdaP = lambda;
    lambda = L + (1-C) * f * sinAlpha *
      (sigma + C*sinSigma*(cos2SigmaM+C*cosSigma*(-1+2*cos2SigmaM*cos2SigmaM)));
  } while (Math.abs(lambda-lambdaP) > 1e-12 && --iterLimit>0);

  if (iterLimit === 0) {
    return NaN;  // formula failed to converge
  }

  let uSq = cosSqAlpha * (a*a - b*b) / (b*b);
  let A = 1 + uSq/16384*(4096+uSq*(-768+uSq*(320-175*uSq)));
  let B = uSq/1024 * (256+uSq*(-128+uSq*(74-47*uSq)));
  let deltaSigma = B*sinSigma*(cos2SigmaM+B/4*(cosSigma*(-1+2*cos2SigmaM*cos2SigmaM)-
    B/6*cos2SigmaM*(-3+4*sinSigma*sinSigma)*(-3+4*cos2SigmaM*cos2SigmaM)));
  let s = b*A*(sigma-deltaSigma);

  //s = s.toFixed(3); // round to 1mm precision
  s = Math.round( s * 1000 ) / 1000; // round to 1mm precision
  return s;

}
